import { useEffect, useState } from "react";
import { DesktopTimePicker, DigitalClock } from "@mui/x-date-pickers";
import type { DesktopTimePickerProps, TimeView } from "@mui/x-date-pickers";
import { forwardRef } from "react";

export type EndTimePickerProps = DesktopTimePickerProps<Date> & {
  name: string;
  formatString?: string;
  setStartDateTime?: any;
  selectedEvent?: any;
  dateTime?: any;
  setValue?: any;
  minTime?: Date | null;
  maxTime?: Date | null;
  onChange?: any;
};

export default function EndTimePicker({
  name,
  setStartDateTime,
  selectedEvent,
  dateTime,
  setValue,
  minTime,
  maxTime,
  onChange,
  ...inpuProps
}: EndTimePickerProps) {
  const [selectedTime, setSelectedTime] = useState<Date>(dateTime);
  const [open, setOpen] = useState(false);
  
  useEffect(() => {
    if (dateTime) {
      setSelectedTime(dateTime);
    }
    //  else {
    //   setSelectedTime(dateTime);
    // }
  }, [dateTime, maxTime]);  

  const getValidHours = () => {
    if (!minTime || !maxTime) return [];
    const hours: number[] = [];

    const minHour = minTime.getHours();
    const maxHour = maxTime.getHours();

    if (maxTime < minTime) {
      for (let i = minHour; i < 24; i++) hours.push(i);
      for (let i = 0; i <= maxHour; i++) hours.push(i);
    } else {
      for (let i = minHour; i <= maxHour; i++) hours.push(i);
    }

    return hours;
  };

  const validHours = getValidHours();
  const hasAM = validHours.some((h) => h < 12);
  const hasPM = validHours.some((h) => h >= 12);

  const shouldDisableTime = (value: Date, view: TimeView) => {
    if (!minTime || !maxTime) return false;

    const hour = value.getHours();
    const minute = value.getMinutes();
    const minHour = minTime.getHours();
    const maxHour = maxTime.getHours();
    const minMinute = minTime.getMinutes();
    const maxMinute = maxTime.getMinutes();

    if (view === "hours") {
      return !validHours.includes(hour);
    }

    if (view === "minutes") {
      if (hour === minHour && minute < minMinute) return true;
      if (hour === maxHour && minute > maxMinute) return true;
      return false;
    }

    return false;
  };

  const CustomLayout = forwardRef<HTMLDivElement, any>((props, ref) => {
    return (
      <div ref={ref}>
        <div style={{ padding: '8px' }}>
          <DigitalClock
            value={props.value}
            onChange={(newValue: Date | null) => {
              if (props.onChange) {
                props.onChange(newValue);
                setOpen(false)
              }
            }}
            minTime={minTime}
            maxTime={maxTime}
            shouldDisableTime={shouldDisableTime}
            skipDisabled
            timeStep={15}
            sx={{
              maxHeight: '300px',
              '& .MuiList-root': {
                maxHeight: '300px',
              },
              '& .MuiButtonBase-root': {
              fontSize: '20px',        
              padding: '10px 14px',    
            },
            }}
          />
        </div>
      </div>
    );
  });

  CustomLayout.displayName = 'CustomLayout';

  return (
    <DesktopTimePicker
      {...inpuProps}
      value={selectedTime}
      minTime={minTime}
      maxTime={maxTime}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={onChange}
      slots={{
        layout: CustomLayout,
      }}
      slotProps={{
        textField: {
          onKeyDown: (e) => {
            e.preventDefault();
          },
          error: false,
          sx: {
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23) !important',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.87) !important',
              },
              '&.Mui-error fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.23) !important',
              },
            },
            '& .MuiFormHelperText-root': {
              display: 'none !important',
            },
            '& .Mui-error': {
              color: 'inherit !important',
            },
          },
        },
        popper: {
          className: !hasPM ? "hide-pm" : !hasAM ? "hide-am" : "",
        },
      }}
      data-testid="controlled-time-picker"
    />
  );
}